import axios from 'axios'
import { apiBaseUrl } from '../const'
import { invalidateAuthCache } from './authValidationCache'
import { invalidateAdminAuthCache } from '../utils/adminAuthCache'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_BASE_URL || apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
})

/**
 * Returns the active token.
 * Priority order:
 * 1. impersonation_token from sessionStorage (impersonation sessions)
 * 2. admin_token from sessionStorage (agent direct login)
 * 3. admin_token from localStorage (admin login)
 */
export const getActiveToken = (): string | null => {
  const impersonationToken = sessionStorage.getItem('impersonation_token')
  if (impersonationToken) {
    return impersonationToken
  }

  const sessionToken = sessionStorage.getItem('admin_token')
  if (sessionToken) {
    return sessionToken
  }

  return localStorage.getItem('admin_token')
}

apiClient.interceptors.request.use(
  (config) => {
    const token = getActiveToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error),
)

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      invalidateAuthCache()
      invalidateAdminAuthCache()
      const isImpersonation =
        !!sessionStorage.getItem('impersonation_session') ||
        !!sessionStorage.getItem('impersonation_token')
      const isAgentDirectLogin =
        !!sessionStorage.getItem('admin_token') &&
        sessionStorage.getItem('role') === 'agent' &&
        !isImpersonation
      const hasImpersonationTraces = localStorage.getItem('impersonation_was_used')

      if (isImpersonation) {
        sessionStorage.removeItem('impersonation_session')
        sessionStorage.removeItem('impersonation_token')
        sessionStorage.removeItem('app_local_theme')
        sessionStorage.removeItem('app_locale')
        console.log('🛡️ AXIOS: Impersonation 401 - cleared sessionStorage only, admin protected')
        window.location.replace('/auth/agent-logout')
      } else if (isAgentDirectLogin) {
        sessionStorage.removeItem('admin_token')
        sessionStorage.removeItem('role')
        sessionStorage.removeItem('app_local_theme')
        sessionStorage.removeItem('app_locale')
        console.log('🔐 AXIOS: Agent direct login 401 - cleared sessionStorage')
        window.location.href = '/auth/login'
      } else if (!hasImpersonationTraces) {
        console.log('🔐 AXIOS: Admin 401 - clearing localStorage')
        localStorage.removeItem('admin_token')
        localStorage.removeItem('role')
        window.location.href = '/auth/login'
      } else {
        console.log('🛡️ AXIOS: Admin 401 with impersonation traces - admin_token protected')
      }
    }
    return Promise.reject(error)
  },
)

export default apiClient
